import { $elementChild } from "@grindex-common/helpers/elements";
import { events } from "@grindex-common/helpers/events";

import { addListener, getNextSibling, getPreviousSibling } from "@grindex-common/helpers/domManipulation";

import { underscoreTemplateGrindex } from "@grindex-client/common/js/helpers/underscore-helper";
import type { SearchQueryFacet } from "@grindex-client/components/Search/Query/FacetQuery";
import type SearchQueryBase from "@grindex-client/components/Search/search-query-base.class";
import { check, styleCheckBoxes, unCheck } from "@grindex-common/helpers/styledCheckBox";
import type { $TSFIXME } from "@grindex-client/common/js/types/typescript-migration";

const SELECTOR_ICON_OPEN = "icon-open";
const SELECTOR_ICON_CLOSED = "icon-closed";

export class FacetsManager<TQuery extends SearchQueryBase> {
	$module: $TSFIXME;
	eventManager: $TSFIXME;
	dirtyManager: $TSFIXME;
	queryObject: TQuery;
	facetsExtensionName: string = "";
	$facetsModule: $TSFIXME;
	$curentFacetTemplate: $TSFIXME;
	$facetElements: $TSFIXME;
	$currentFiltersContainer: $TSFIXME;
	$curentSelectedFacetTemplate: $TSFIXME;
	$rangedFacets: $TSFIXME;
	facetTemplate: $TSFIXME;
	facetChildTemplate: $TSFIXME;
	facetids: $TSFIXME;
	constructor($ownerModule: HTMLElement, eventManager, dirtyManager, queryObject: TQuery) {
		this.$module = $ownerModule;
		this.eventManager = eventManager;
		this.dirtyManager = dirtyManager;
		this.queryObject = queryObject;
	}

	initFacets() {
		this.facetsExtensionName = "Facets";
		this.$facetsModule = $elementChild(this.$module, "search-facets");

		this.eventManager.on(this, events.search.RENDER, this.render.bind(this));

		this.$curentFacetTemplate = underscoreTemplateGrindex(document.querySelector("#current-facets-filters-tmpl").innerHTML);
		this.$facetElements = this.$facetsModule.querySelectorAll(".styledCheckbox");
		this.$currentFiltersContainer = this.$module.querySelector(".js-current-filters");
		this.$curentSelectedFacetTemplate = underscoreTemplateGrindex($("#current-facets-filters-tmpl").html());
		this.$rangedFacets = this.$facetsModule.querySelectorAll(".ranged-facets");

		this.facetTemplate =
			typeof window.config.search.htmlReservoirTemplate !== "undefined"
				? underscoreTemplateGrindex(document.getElementById("#" + window.config.search.htmlReservoirTemplate).html())
				: underscoreTemplateGrindex($("#facet-group-tmpl").html());

		this.facetChildTemplate = underscoreTemplateGrindex($("#facet-child-group-tmpl").html());

		this.initializeRangedFacets();

		// add click functionality only to those headers which have a .group after them
		this.$facetsModule.querySelectorAll(".facet-group-header").forEach(($header) => {
			if ($header.classList.contains("default-open")) {
				$header.classList.add(SELECTOR_ICON_OPEN);
			}
		});

		this.openCloseAccordion();

		styleCheckBoxes(this.$facetsModule.querySelectorAll("input[type=checkbox]"));

		this.bindInitialFacetEvents();

		this.queryObject.setCategories();
	}

	hasCurrentFilters() {
		return this.$currentFiltersContainer ? true : false;
	}

	bindInitialFacetEvents() {
		this.eventManager.on(this, events.search.FACET_CLEAR_ALL, this.clearSelectedFacets.bind(this));
		this.eventManager.on(this, events.search.RENDERED, this.bindRemoveFacetButtons.bind(this));
	}

	bindFacetEvents() {
		addListener(this.$facetsModule.querySelectorAll(".styledCheckbox"), "click", this.facetToggle.bind(this));
		addListener(this.$facetsModule.querySelectorAll(".facet-group-header"), "click", this.filterToggle.bind(this));
		addListener(this.$facetsModule.querySelectorAll(".facet-sub-header label"), "click", this.filterToggleSubCategory);
		addListener(this.$facetsModule.querySelectorAll(".no-child-facet"), "click", this.noChildFacet.bind(this));
	}

	bindRemoveFacetButtons() {
		addListener(this.$module.querySelectorAll(".js-clear-currentcategory"), "click", this.clearSelectedFacet.bind(this));
	}

	removeLegacyVisibilityRules() {
		const $outerGroups = this.$facetsModule.getElementsByClassName("outergroup");

		$outerGroups.forEach(($outerGroup) => {
			if ($outerGroup.classList.contains("d-none")) {
				$outerGroup.style.display = "block";
				$outerGroup.classList.add("hidden");
			}
		});
	}

	initializeRangedFacets() {
		if (this.$rangedFacets.length === 0) {
			return;
		}
		const event = new Event(events.search.FACET_CLICK);
		this.$rangedFacets.forEach(($rangedFacet) => {
			addListener($rangedFacet.querySelectorAll(".facet-group-header"), "click", this.filterToggle);
			addListener($rangedFacet.querySelectorAll("input"), "change", function ($element) {
				$element.dispatchEvent(event);
			});
		});
	}

	render() {
		const openHeaderGroups = [];
		const _this = this;
		this.facetids = this.queryObject.queryData.facetids;

		document.querySelectorAll(".icon-open").forEach(($group) => {
			$group.querySelectorAll(".facet-group").forEach(($facetGroup) => {
				openHeaderGroups.push($facetGroup.innerText);
			});
		});

		const markup = this.facetTemplate({
			isChecked: (facetId) => {
				const facetids = this.queryObject.queryData.facetids;
				if (Array.isArray(facetids)) {
					return facetids.includes(facetId) || facetids.includes(facetId.toString());
				} else {
					return facetids === facetId;
				}
			},
			isHeaderClosed: (facetGroupName, facetids) => {
				const selectedfacetids = this.facetids;
				if (_this.isBrandPage()) {
					if (openHeaderGroups.includes(facetGroupName)) {
						return false;
					}
					return !facetids.some(function (facetId) {
						if (Array.isArray(selectedfacetids)) {
							return selectedfacetids.includes(facetId.Id);
						} else {
							return selectedfacetids === facetId.Id;
						}
					});
				} else if (openHeaderGroups.length > 0) {
					return !openHeaderGroups.includes(facetGroupName);
				}
				return true;
			},
			renderFacets: this.facetChildTemplate,
			filterelement: (facetGroups, parentId) => {
				const filterFacetGroup = [];
				facetGroups.forEach(function (facet) {
					if (facet.ParentId === parentId) {
						filterFacetGroup.push(facet);
					}
				});

				return filterFacetGroup;
			},
			hideFacet: () => {
				return false;
			},
			...this.queryObject.data,
		});

		this.$facetsModule.innerHTML = markup;

		styleCheckBoxes(this.$facetsModule.querySelectorAll("input[type=checkbox]"));

		this.checkFacets();

		this.removeLegacyVisibilityRules();

		this.openCloseAccordion();

		this.bindFacetEvents();

		this.setFacetsDirtyStatus();
	}

	renderCurrentFilters() {
		if (this.hasCurrentFilters()) {
			this.$currentFiltersContainer.innerHTML = this.$curentSelectedFacetTemplate(this.queryObject.data);
			this.bindRemoveFacetButtons();
		}
	}

	facetToggle(e) {
		const $facet = e.currentTarget.querySelector("input");

		this.changeFacetState($facet.dataset.id.toString(), $facet.checked);
	}

	changeFacetState(id: string, state: boolean) {
		const facetData: SearchQueryFacet = {
			id: id,
			selected: state,
		};

		this.queryObject.updateFacets(facetData);
		this.eventManager.trigger(events.search.SEARCH);
	}

	filterToggleSubCategory(e) {
		const $subCategory = getNextSibling(e.currentTarget.parentElement, ".subcategorygroup");

		if ($subCategory) {
			$subCategory.classList.toggle("hidden");
		}
	}

	filterToggle(e) {
		const $outerGroup = getNextSibling(e.currentTarget, ".outergroup");
		if ($outerGroup) {
			e.currentTarget.classList.toggle(SELECTOR_ICON_CLOSED);
			e.currentTarget.classList.toggle(SELECTOR_ICON_OPEN);
			$outerGroup.classList.toggle("hidden");
		}
	}

	openCloseAccordion() {
		this.$facetsModule.querySelectorAll(".facet-sub-header").forEach(($subHeader) => {
			$subHeader.nextElementSibling.querySelectorAll(".subcategorygroup").forEach(($subCategory) => {
				$subCategory.classList.add("hidden");
			});
		});

		const $outerGroups = this.$facetsModule.querySelectorAll(".outergroup");

		$outerGroups.forEach(($outerGroup) => {
			if ($outerGroup.querySelectorAll("input:checked").length) {
				$outerGroup.classList.remove("hidden");
				const $facetHeader = getPreviousSibling($outerGroup, ".facet-group-header");
				$facetHeader.classList.remove(SELECTOR_ICON_CLOSED);
				$facetHeader.classList.add(SELECTOR_ICON_OPEN);
			}
		});

		this.$facetsModule.querySelectorAll(".subcategorygroup").forEach(($subCategoryGroup) => {
			if ($subCategoryGroup.querySelectorAll("input:checked").length) {
				const $subHeaders = $subCategoryGroup.querySelectorAll(".facet-sub-header");

				if ($subHeaders) {
					$subHeaders.forEach(($header) => {
						const $subCategory = getNextSibling($header, ".subcategorygroup");
						if ($subCategory) {
							$subCategory.classList.remove("hidden");
						}
					});
				}
			}
		});
	}

	noChildFacet(e) {
		const $selectedFacet = $(e.currentTarget).children().children("input");

		if (e.target.className !== "styledCheckbox") {
			$selectedFacet.prop("checked", !$selectedFacet.prop("checked"));

			styleCheckBoxes(this.$facetsModule.querySelectorAll("input[type=checkbox]"));

			const eventData: SearchQueryFacet = {
				id: $selectedFacet.data("id"),
				selected: $selectedFacet.is(":checked"),
			};

			const event = new CustomEvent(events.search.FACET_CLICK, { detail: eventData });

			this.$module.dispatchEvent(event);
		}
	}

	isBrandPage() {
		const brandName = window.config.brand.name;
		return brandName && brandName.length > 0;
	}

	checkFacets() {
		const $checkBoxes = this.$facetsModule.querySelectorAll("input[type='checkbox']");

		$checkBoxes.forEach(($checkBox) => {
			if (this.queryObject.queryData.facetids.includes($checkBox.dataset.id)) {
				check($checkBox);
			} else {
				unCheck($checkBox);
			}
		});

		this.setFacetsDirtyStatus();
	}

	unSelectCheckBoxes() {
		Array.prototype.filter
			.call(this.$facetsModule.querySelectorAll(".facet input[type=checkbox]"), ($facet) => {
				return $facet.checked;
			})
			.forEach(($checkedFacet) => {
				unCheck($checkedFacet);
			});
	}

	clearSelectedFacet(currentFacet) {
		const $selectedFacet = currentFacet.target;
		const selectedFacetValue = $selectedFacet.dataset.value;
		const selectedId = $selectedFacet.dataset.id;

		if (selectedFacetValue === "clearAllFilters") {
			this.clearSelectedFacets();
		} else if (parseInt(selectedId) >= 0) {
			this.changeFacetState(selectedId, false);
			this.eventManager.trigger(events.search.FACET_CLEAR, selectedId);
		}

		this.setFacetsDirtyStatus();
	}

	clearSelectedFacets() {
		this.queryObject.clearFacets();

		this.unSelectCheckBoxes();

		document.querySelector(".clear-facets-btn").classList.remove("disabled");

		this.setFacetsDirtyStatus();
		this.eventManager.trigger(events.search.SEARCH);
	}

	setFacetsDirtyStatus() {
		if (this.queryObject.hasFacetIds()) {
			this.dirtyManager.setDirty(this.facetsExtensionName);
		} else {
			this.dirtyManager.removeDirty(this.facetsExtensionName);
		}
	}
}
